import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatearFechaHora } from "@/lib/catalogos";
import {
  autorizarCorreo,
  listarAccesos,
  revocarAcceso,
} from "@/lib/administradores.functions";

export const Route = createFileRoute("/_authenticated/admin/equipo")({
  component: Equipo,
});

function Equipo() {
  const queryClient = useQueryClient();
  const [correo, setCorreo] = useState("");
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "equipo"],
    queryFn: () => listarAccesos(),
  });

  const refrescar = () => queryClient.invalidateQueries({ queryKey: ["admin", "equipo"] });

  const autorizar = useMutation({
    mutationFn: () => autorizarCorreo({ correo }),
    onSuccess: (resultado) => {
      setCorreo("");
      refrescar();
      toast.success(
        resultado.activado
          ? "Acceso activado: la persona ya puede entrar al panel."
          : "Correo autorizado. Podrá entrar en cuanto cree su cuenta.",
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revocar = useMutation({
    mutationFn: (correoObjetivo: string) => revocarAcceso({ correo: correoObjetivo }),
    onSuccess: () => {
      refrescar();
      toast.success("Acceso retirado.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl">Equipo administrativo</h1>
        <p className="text-sm text-muted-foreground">
          Solo los correos autorizados aquí pueden entrar al panel privado de MEDAM.
        </p>
      </div>

      <section className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
        <Label htmlFor="correo-admin">Autorizar un correo</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          Puedes autorizarlo antes de que la persona cree su cuenta; el acceso se activa en su
          primer inicio de sesión.
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <Input
            id="correo-admin"
            type="email"
            placeholder="persona@medam.mx"
            value={correo}
            onChange={(evento) => setCorreo(evento.target.value)}
          />
          <Button
            onClick={() => autorizar.mutate()}
            disabled={autorizar.isPending || !correo.includes("@")}
          >
            {autorizar.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
            Autorizar
          </Button>
        </div>
      </section>

      <section className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
        <h2 className="font-display text-lg">Accesos autorizados</h2>
        {isLoading ? (
          <Loader2 className="mt-4 size-5 animate-spin text-primary" />
        ) : error ? (
          <p className="mt-3 text-sm text-destructive">{(error as Error).message}</p>
        ) : (
          <ul className="mt-4 divide-y divide-border text-sm">
            {(data ?? []).map((acceso) => (
              <li key={acceso.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <span className="flex items-center gap-2 font-medium">
                  {acceso.correo}
                  {acceso.es_maestra ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                      <ShieldCheck className="size-3" /> Cuenta maestra
                    </span>
                  ) : null}
                </span>
                <span className="text-xs text-muted-foreground">
                  {acceso.activo ? "Acceso activo" : "Pendiente de crear cuenta"} · Autorizado el{" "}
                  {formatearFechaHora(acceso.created_at)}
                </span>
                {acceso.es_maestra ? null : (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={revocar.isPending}
                    onClick={() => revocar.mutate(acceso.correo)}
                  >
                    <Trash2 className="mr-1 size-4" /> Retirar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
